import { ITemplateDocument } from "@rentiohq/shared-frontend/dist/reduxV2/templateDocuments/templateDocument.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  EDocumentCategory,
  IDocument,
} from "@rentiohq/shared-frontend/dist/types/document.types";
import { compact } from "lodash";

export const getDocumentActivityFilter = (params: { document: IDocument }) => {
  const { document } = params;

  // TODO:
  // If backend would log activities for documents ((e.g. doc shared with X member)) itself we can remove this check,
  // for now only show the history if theres a linked document package
  if (!document?.documentPackageId) {
    return;
  }

  return {
    where: {
      or: compact([
        { documentId: document.id },
        { documentPackageId: document.documentPackageId },
      ]),
    },
  };
};

const isUploadedInspectionReport = (document: IDocument) =>
  !document.templateDocumentId &&
  document.categoryId === EDocumentCategory.LocationDescription;

/**
 * The lease to open the signing wizard with (`/forms/document-package`'s
 * `contractId`). A template-generated document keeps its template's lease when
 * the caller passes the template (the drawer); the document list has none to
 * pass, so it gets no lease, as before. Of the uploaded documents only a
 * property inspection report takes its own lease along, so the wizard can offer
 * to register it with MyRent against that lease; every other upload opens the
 * wizard without one.
 *
 * `linkedContract` is the document's lease as the caller loaded it. The wizard
 * shows a spinner until the lease in its URL has loaded, so a lease that is not
 * loaded - deleted, not readable by this user, or still being fetched - is left
 * out and the wizard opens without it.
 */
export const getContractIdToSign = (params: {
  document: IDocument;
  templateDocument?: ITemplateDocument;
  linkedContract?: IContract;
}) => {
  const { document, templateDocument, linkedContract } = params;

  if (templateDocument) {
    return templateDocument.contractId || undefined;
  }

  if (!isUploadedInspectionReport(document)) {
    return;
  }

  // The v2 document API sends the lease as `contractIds`, never `contractId`.
  const contractId = document.contractIds?.[0];
  return linkedContract?.id === contractId ? contractId : undefined;
};

/**
 * Whether to wait before offering to sign: an uploaded inspection report whose
 * lease is still being fetched would otherwise open the wizard without it (see
 * getContractIdToSign). A failed fetch is no longer fetching, so the wizard then
 * opens without the lease instead of waiting forever.
 */
export const isFetchingContractToSign = (params: {
  document: IDocument;
  isFetchingLinkedContract?: boolean;
}) =>
  !!params.isFetchingLinkedContract &&
  isUploadedInspectionReport(params.document);
